import {
  ColorClass,
  Icon,
  IconIdentifier,
  Input,
  InputGroup,
  MeatBallsMenu,
  MeatBallsMenuDirection,
  Placement,
  Tooltip,
} from '@aus-platform/design-system';
import { SceneMode } from 'cesium';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { filter, sortBy } from 'lodash';
import {
  removeWorkspaceLayersFromMap,
  updateWorkspaceLayerZIndices,
} from 'map-3d/components/map-3d-sidebar/components/map-3d-workspace/shared';
import type {
  WorkspaceLayer,
  WorkspaceLayerListObj,
} from 'map-3d/components/map-3d-sidebar/shared';
import {
  removeWorkspaceLayers,
  resetPropertiesLayer,
  selectMap3DState,
  selectMap3dWorkspace,
  setCurrentEditableLayerId,
  setShowUnsavedFeaturesModal,
  updateWorkspaceLayer,
} from 'map-3d/shared/map-3d-slices';
import { useRef } from 'react';
import { LayerType } from 'shared/api';

type LayerMeatBallMenuProps = {
  layer: WorkspaceLayer;
  showFeatureCount: boolean;
  setShowFeatureCount: React.Dispatch<React.SetStateAction<boolean>>;
  setShowSelectAreaCategoryModal: React.Dispatch<React.SetStateAction<boolean>>;
  showSublayers?: boolean;
  setShowSublayers?: React.Dispatch<React.SetStateAction<boolean>>;
  isClampEnabled?: boolean;
  handleClampToSurfaceChange?: (next: boolean) => void;
};

export const LayerMeatBallMenu: React.FC<LayerMeatBallMenuProps> = ({
  layer,
  showFeatureCount,
  setShowFeatureCount,
  setShowSelectAreaCategoryModal,
  showSublayers,
  setShowSublayers,
  isClampEnabled = false,
  handleClampToSurfaceChange,
}) => {
  // Refs.
  const checkboxRef = useRef<HTMLInputElement>(null);
  const clampToSurfaceRef = useRef<HTMLInputElement>(null);

  // Dispatch.
  const dispatch = useAppDispatch();

  // Selectors.
  const {
    currentEditableLayerId,
    drawingTools,
    actionTools,
    workspaceLayers,
    polygonAiConfig: polygonAi,
    currentPropertyLayerId,
  } = useAppSelector(selectMap3dWorkspace);

  const { cesiumProxy, currentSceneMode } = useAppSelector(selectMap3DState);

  // Constants.
  const isVisible = layer.show;
  const isIn2DView = currentSceneMode === SceneMode.SCENE2D;
  const { isAiModeEnabled, activeOrthoLayerId } = polygonAi;
  const isRemoveFromWorkspaceDisabled =
    isAiModeEnabled && layer.id === activeOrthoLayerId;

  // Handlers.
  const goToLayer = () => {
    const newTab = window.open(
      `/layers?iterationId=${layer.iteration?.id}&highlightLayer=${layer.id}`,
      '_blank',
    );
    newTab?.focus();
  };

  const setLayer = (layer: WorkspaceLayerListObj) => {
    dispatch(updateWorkspaceLayer(layer));
  };

  const onClickRemoveFromWorkspace = (id: string, layer: WorkspaceLayer) => {
    // If Toggled layer is the currently active editable layer.
    if (id === currentEditableLayerId) {
      if (drawingTools?.checkUnsavedFeatures()) {
        dispatch(setShowUnsavedFeaturesModal(true));
        return;
      }
      dispatch(setCurrentEditableLayerId(null));

      // Clear current state of all tools.
      drawingTools?.resetAllDrawnFeatureArrays();

      // Deactivate all tools.
      drawingTools?.deactivateAllTools();
      actionTools?.deactivateAllTools();
    }

    removeWorkspaceLayersFromMap([layer], drawingTools, cesiumProxy);
    dispatch(removeWorkspaceLayers([layer.id]));

    if (id === currentPropertyLayerId) {
      dispatch(resetPropertiesLayer());
    }
    // Filtering out the current layer (b/c we won't have the updated "workspaceLayers" redux state)
    const activeLayers = filter(workspaceLayers, (currentLayer) => {
      return currentLayer.id !== id;
    });

    const sortedActiveLayers = sortBy(activeLayers, [
      (layer) => -layer.zIndex!,
    ]);

    if (cesiumProxy) {
      updateWorkspaceLayerZIndices(sortedActiveLayers, setLayer);
    }
  };

  const onClickSelectAreaCategory = () => {
    setShowSelectAreaCategoryModal(true);
  };

  return (
    <MeatBallsMenu
      className="layer-meatball-menu"
      drop={MeatBallsMenuDirection.Down}
      onClick={(event) => {
        event.stopPropagation();
      }}
      data-testid="meatball-menu-button"
    >
      <Tooltip
        className="layer-meatball-menu__remove-from-workspace-tooltip"
        hoverText={`${layer.name} is currently being used for AI digitisation. Exit AI Mode to remove it from the workspace.`}
        show={isRemoveFromWorkspaceDisabled ? undefined : false}
      >
        <MeatBallsMenu.Item
          onClick={() => onClickRemoveFromWorkspace(layer.id, layer)}
          disabled={
            layer.id === currentEditableLayerId || isRemoveFromWorkspaceDisabled
          }
          data-testid="remove-from-workspace-action"
        >
          Remove from workspace
        </MeatBallsMenu.Item>
      </Tooltip>
      {layer.type === LayerType.Cad && isVisible && (
        <MeatBallsMenu.Item
          onClick={(event) => {
            event.stopPropagation();
            checkboxRef.current?.click();
          }}
        >
          <InputGroup>
            <Input.Label>Show Sub Layers</Input.Label>
            <Input.CheckBox
              checked={showSublayers}
              ref={checkboxRef}
              onClick={(event) => {
                event.stopPropagation();
                setShowSublayers?.((prev) => !prev);
              }}
            />
          </InputGroup>
        </MeatBallsMenu.Item>
      )}
      {layer.type === LayerType.Cad && isVisible && (
        <Tooltip
          hoverText={
            isIn2DView
              ? 'Clamp to Surface cannot be modified in 2D view.'
              : 'Visually clamp this layer onto the terrain surface without changing its original elevation data.'
          }
          placement={Placement.Right}
        >
          <MeatBallsMenu.Item
            onClick={(event) => {
              event.stopPropagation();
              clampToSurfaceRef.current?.click();
            }}
          >
            <InputGroup>
              <Input.Label>Clamp to Surface</Input.Label>
              <Input.CheckBox
                ref={clampToSurfaceRef}
                checked={isClampEnabled}
                disabled={isIn2DView}
                onClick={() => {
                  if (!isIn2DView) {
                    handleClampToSurfaceChange?.(!isClampEnabled);
                  }
                }}
              />
            </InputGroup>
          </MeatBallsMenu.Item>
        </Tooltip>
      )}
      {(layer.type === LayerType.Vector ||
        layer.type === LayerType.PointCloud) &&
        isVisible && (
          <MeatBallsMenu.Item
            onClick={(event) => {
              event.stopPropagation();
              checkboxRef.current?.click();
            }}
          >
            <InputGroup>
              <Input.Label>Show Feature Count</Input.Label>
              <Input.CheckBox
                checked={showFeatureCount}
                ref={checkboxRef}
                onClick={(event) => {
                  event.stopPropagation();
                  setShowFeatureCount(!showFeatureCount);
                }}
              />
            </InputGroup>
          </MeatBallsMenu.Item>
        )}
      {layer.type === LayerType.Vector && layer.canManageLayer && isVisible && (
        <MeatBallsMenu.Item
          onClick={onClickSelectAreaCategory}
          data-testid="select-area-category-action"
        >
          Select an Area Category
        </MeatBallsMenu.Item>
      )}
      {layer.type !== LayerType.CapturedDsm && (
        <MeatBallsMenu.Item
          onClick={goToLayer}
          className="layer-meatball-menu__go-to-layer"
          data-testid="goto-layer-page-action"
        >
          Go to Layer Page
          <Icon
            colorClass={ColorClass.Neutral200}
            identifier={IconIdentifier.ArrowUpRight}
            size={18}
          />
        </MeatBallsMenu.Item>
      )}
    </MeatBallsMenu>
  );
};
